import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button, Row, Col } from "react-bootstrap";
import { FaThumbsUp } from "react-icons/fa6";
import axios from 'axios';
import JoinTeamModal from './JoinTeamModal';
import AddTeamModal from '../teams/AddTeamModal';
import { useDebouncedCallback } from '../utils/debouncUtil';

function ProfileFeed({ user, activeLeague, activeSeason, registrationSeason }) {
	const [games, setGames] = useState([]);
	const [userLeagues, setUserLeagues] = useState([]);
	const [teams, setTeams] = useState([]);
	const [currentSeasonUserTeams, setCurrentSeasonUserTeams] = useState([]);
	const [nextEvent, setNextEvent] = useState(null);
	const [nextEventLoading, setNextEventLoading] = useState(false);
	const [showJoinTeamModal, setShowJoinTeamModal] = useState(false);
	const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
	const [justAddedTeam, setJustAddedTeam] = useState(false);

	// Pagination state for upcoming games
	const [currentPage, setCurrentPage] = useState(1);
	const [hasMoreGames, setHasMoreGames] = useState(true);
	const [loadingMoreGames, setLoadingMoreGames] = useState(false);
	const [totalGames, setTotalGames] = useState(0);

	const playerIds = (user?.players || []).map((player) => player.id);
	const activeSeasonParticipants = activeSeason?.participants || [];
	const registrationSeasonParticipants = registrationSeason?.participants || [];

	// Check if user has any players registered in the selected active season.
	const hasPlayersInCurrentSeason = activeSeasonParticipants.some((participant) =>
		playerIds.includes(participant.id)
	);
	const hasPlayersInRegistrationSeason = registrationSeasonParticipants.some((participant) =>
		playerIds.includes(participant.id)
	);

	const hasCurrentSeasonTeam = currentSeasonUserTeams.length > 0 || justAddedTeam;
	const canCreateTeam =
		Boolean(activeSeason?.id) &&
		hasPlayersInCurrentSeason &&
		!hasCurrentSeasonTeam;
	const canJoinTeam = hasPlayersInCurrentSeason && !hasCurrentSeasonTeam && teams.length > 0;
	const inTeam = hasCurrentSeasonTeam;

	useEffect(() => {
		// Fetch user's leagues
		axios.get('/api/leagues/user-leagues/', {
			withCredentials: true,
		})
			.then(response => {
				setUserLeagues(response.data);
			})
			.catch(error => {
				console.error('Error fetching user leagues:', error);
			});
	}, []);

	useEffect(() => {
		setJustAddedTeam(false);
	}, [activeSeason?.id]);

	useEffect(() => {
		if (!activeSeason?.id) {
			setNextEvent(null);
			setNextEventLoading(false);
			return;
		}

		let cancelled = false;
		setNextEventLoading(true);
		axios.get('/api/leagues/events/next_event/', {
			params: { season_id: activeSeason.id },
			withCredentials: true,
		})
			.then(response => {
				if (cancelled) return;
				setNextEvent(response.data?.event_type ? response.data : null);
			})
			.catch(error => {
				if (cancelled) return;
				if (error.response?.status === 404) {
					setNextEvent(null);
					return;
				}
				console.error('Error fetching next event:', error);
				setNextEvent(null);
			})
			.finally(() => {
				if (!cancelled) {
					setNextEventLoading(false);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [activeSeason?.id]);

	useEffect(() => {
		// Fetch teams for the active season
		if (activeSeason?.id) {
			Promise.all([
				axios.get('/api/teams/', {
					params: { season: activeSeason.id },
					withCredentials: true,
				}),
				axios.get('/api/teams/user-teams/', {
					params: { season: activeSeason.id },
					withCredentials: true,
				}),
			])
				.then(([teamsResponse, userTeamsResponse]) => {
					setTeams(teamsResponse.data.results || teamsResponse.data);
					setCurrentSeasonUserTeams(userTeamsResponse.data);
				})
				.catch(error => {
					console.error('Error fetching teams:', error);
				});
		} else {
			setTeams([]); // Clear teams if no active season
			setCurrentSeasonUserTeams([]);
		}
	}, [activeSeason?.id]);

	useEffect(() => {
		if (activeSeason?.id && inTeam && userLeagues.length > 0) {
			axios.get(`/api/games/upcoming/`, {
				params: {
					page: 1,
					limit: 3,
					season_id: activeSeason.id,
				},
				withCredentials: true,
			})
				.then(response => {
					// Handle both paginated and non-paginated responses
					let gamesData;
					if (response.data.results) {
						gamesData = response.data.results;
					} else if (Array.isArray(response.data)) {
						gamesData = response.data;
					} else {
						gamesData = [];
					}

					setGames(gamesData);
					setCurrentPage(1);
					setHasMoreGames(response.data.next !== null);
					setTotalGames(response.data.count || 0);
				})
				.catch(error => {
					console.error('Error fetching games:', error);
					setGames([]);
					setCurrentPage(1);
					setHasMoreGames(false);
					setTotalGames(0);
				});
		} else {
			setGames([]);
			setCurrentPage(1);
			setHasMoreGames(true);
			setTotalGames(0);
		}
	}, [activeSeason?.id, inTeam, userLeagues.length]);

	const handleAttendingToggle = async (gameId, participantId, currentAttending) => {
		try {
			const response = await axios.patch(
				`/api/games/${gameId}/attending/`,
				{ attending: !currentAttending },
				{
					withCredentials: true,
				}
			);

			if (response.status === 200) {
				// Update local state
				setGames((prevGames) =>
					prevGames.map((game) =>
						game.id === gameId
							? {
								...game,
								players: game.players?.map((p) =>
									p.id === participantId ? { ...p, attending: !currentAttending } : p
								) || [],
							}
							: game
					)
				);
			}
		} catch (error) {
			console.error("Error updating attending status:", error);
		}
	};

	const handleJoinTeamSuccess = () => {
		// Refresh the teams list
		if (activeSeason?.id) {
			Promise.all([
				axios.get('/api/teams/', {
					params: { season: activeSeason.id },
					withCredentials: true,
				}),
				axios.get('/api/teams/user-teams/', {
					params: { season: activeSeason.id },
					withCredentials: true,
				}),
			])
				.then(([teamsResponse, userTeamsResponse]) => {
					setTeams(teamsResponse.data.results || teamsResponse.data);
					setCurrentSeasonUserTeams(userTeamsResponse.data);
				})
				.catch(error => {
					console.error('Error fetching teams:', error);
				});
		}
	};

	const handleTeamAdded = (newTeam) => {
		// hide the modal
		setShowCreateTeamModal(false);
		// mark that we're in a team now
		setJustAddedTeam(true);
		// refresh the list too, if you want to show it somewhere
		if (activeSeason?.id) {
			Promise.all([
				axios.get('/api/teams/', {
					params: { season: activeSeason.id },
					withCredentials: true,
				}),
				axios.get('/api/teams/user-teams/', {
					params: { season: activeSeason.id },
					withCredentials: true,
				}),
			]).then(([teamsResponse, userTeamsResponse]) => {
				setTeams(teamsResponse.data.results || teamsResponse.data);
				setCurrentSeasonUserTeams(userTeamsResponse.data);
			})
				.catch(e => console.error('Error fetching teams:', e));
		}
	};

	const loadMoreGames = async () => {
		if (loadingMoreGames || !hasMoreGames || !activeSeason?.id) return;

		setLoadingMoreGames(true);
		try {
			const response = await axios.get(`/api/games/upcoming/`, {
				params: {
					page: currentPage + 1,
					limit: 3,
					season_id: activeSeason.id,
				},
				withCredentials: true,
			});

			const newGames = response.data.results || response.data;
			const paginationInfo = response.data;

			// Update games state by appending new games
			setGames(prevGames => [...prevGames, ...newGames]);

			// Update pagination state
			setCurrentPage(prev => prev + 1);
			setHasMoreGames(paginationInfo.next !== null);
			setTotalGames(paginationInfo.count || 0);
		} catch (error) {
			console.error('Error loading more games:', error);
		} finally {
			setLoadingMoreGames(false);
		}
	};

	// Debounced function for loading more games
	const debouncedLoadMore = useDebouncedCallback(() => {
		if (hasMoreGames && !loadingMoreGames) {
			loadMoreGames();
		}
	}, 300);

	const handleLoadMoreGames = () => {
		debouncedLoadMore();
	};

	return (
		<div className="p-0">
			{/* Sign Up Card - Show only when the backend says this league has an open registration season */}
			{registrationSeason && !hasPlayersInRegistrationSeason && (
				<div className="col-12">
					<div className="card mb-3">
						<div className="card-body">
							<h5 className="card-title">Ready to Play?</h5>
							<p className="card-text">
								{registrationSeason.name} is currently open for registration.
							</p>
							<a
								href={`/league/${registrationSeason.id}/register/`}
								className="btn btn-primary"
							>
								Sign Up Now
							</a>
						</div>
					</div>
				</div>
			)}

			{/* Join/Create Team Card - Show if the user is not already on the current season's team */}
			{canCreateTeam && (
				<div className="col-12">
					<div className="card mb-3">
						<div className="card-body text-center">
							<h5 className="card-title">
								{canJoinTeam ? 'Join a Team' : 'Create a Team'}
							</h5>

							{hasPlayersInCurrentSeason ? (
								teams.length > 0 ? (
									<p className="card-text">
										{teams.length === 1
											? "There's one team looking for players!"
											: `There are ${teams.length} teams looking for players!`}
									</p>
								) : (
									<p className="card-text">
										No teams have been created for this season yet.
									</p>
								)
							) : (
								<p className="card-text">
									You are not on a team for the current season. You can create one from here.
								</p>
							)}

							{/* Actions */}
							<Row className="justify-content-center align-items-center mt-3">
								{canJoinTeam && (
									<>
										<Col xs="auto">
											<Button
												variant="primary"
												size="sm"
												onClick={() => setShowJoinTeamModal(true)}
											>
												View Teams
											</Button>
										</Col>

										<Col xs="auto">
											<span className="fw-semibold mx-3">or</span>
										</Col>
									</>
								)}

								<Col xs="auto">
									<Button
										variant="secondary"
										size="sm"
										onClick={() => setShowCreateTeamModal(true)}
									>
										Create a Team
									</Button>
								</Col>
							</Row>
						</div>
					</div>
				</div>
			)}

			{/* Join Team Modal */}
			<JoinTeamModal
				isOpen={showJoinTeamModal}
				onClose={(success) => {
					setShowJoinTeamModal(false);
					if (success) {
						handleJoinTeamSuccess();
					}
				}}
				leagueId={activeLeague?.id}
				seasonId={activeSeason?.id}
			/>

			{/* Rest of your feed content */}
			<div className="col-12">
				{/* Events START */}
				<div className="card">
					<div className="card-header d-sm-flex align-items-center justify-content-between border-0 pb-0">
						<h5 className="card-title mb-sm-0">
							{nextEventLoading ? (
								<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
							) : nextEvent ? `Next ${nextEvent.event_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} Event` : 'Next Event'}
						</h5>
					</div>
					<div className="card-body pt-1">
						{nextEventLoading ? (
							<div className="row align-items-center mb-4 mb-lg-2 pb-4 pb-lg-2">
								<div className="col-4 col-lg-2 order-1">
									<div className="avatar avatar-xl">
										<div className="position-relative">
											<div
												style={{
													width: '100px',
													height: '100px',
													margin: 'auto',
													display: 'block',
													position: 'relative',
													overflow: 'hidden',
													borderRadius: '8px',
													backgroundColor: '#f8f9fa',
													border: '2px solid #dee2e6',
												}}
											>
												<div className="d-flex align-items-center justify-content-center h-100">
													<div className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div className="col-8 col-lg-10 order-3 order-lg-2">
									<div className="justify-content-center mt-2 mt-sm-0">
										<div className="placeholder-glow" aria-hidden="true">
											<div className="placeholder col-8 mb-1"></div>
											<div className="placeholder col-6 mb-1"></div>
											<div className="placeholder col-4"></div>
										</div>
									</div>
								</div>
							</div>
						) : nextEvent ? (
							<div className="row align-items-center mb-4 mb-lg-2 pb-4 pb-lg-2">
								<div className="col-4 col-lg-2 order-1">
									<div className="avatar avatar-xl">
										<div className="position-relative">
											<div
												style={{
													width: '100px',
													height: '100px',
													margin: 'auto',
													display: 'block',
													position: 'relative',
													overflow: 'hidden',
													borderRadius: '8px',
													backgroundColor: '#f8f9fa',
													border: '2px solid #dee2e6',
												}}
											>
												{nextEvent.sponsor && nextEvent.sponsor.logo_url ? (
													<img
														src={nextEvent.sponsor.logo_url}
														alt={`${nextEvent.sponsor.name} logo`}
														style={{
															width: '100%',
															height: '100%',
															objectFit: 'contain',
															padding: '8px',
														}}
														onError={(e) => {
															// Hide the image and show the fallback icon if the image fails to load
															e.target.style.display = 'none';
															e.target.nextSibling.style.display = 'flex';
														}}
													/>
												) : null}
												<div
													className="d-flex align-items-center justify-content-center h-100"
													style={{
														display: nextEvent.sponsor && nextEvent.sponsor.logo_url ? 'none' : 'flex'
													}}
												>
													<i className="bi bi-calendar-event" style={{ fontSize: '2rem', color: '#6c757d' }}></i>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div className="col-8 col-lg-10 order-3 order-lg-2">
									<div className="justify-content-center mt-2 mt-sm-0">
										<h5 className="mb-1">
											{nextEvent.sponsor && nextEvent.sponsor.website ? (
												<Link className="nav-link" to={`${nextEvent.sponsor.website}`} target="_blank">
													{nextEvent.title}
												</Link>
											) : (
												<span className="nav-link">{nextEvent.title}</span>
											)}
										</h5>
										<ul className="nav nav-stack small">
											<li className="nav-item">
												<i className="bi bi-calendar-check pe-1"></i> {nextEvent.formatted_datetime}
											</li>
											<li className="nav-item">
												<i className="bi bi-geo-alt pe-1"></i> {nextEvent.location_name || nextEvent.full_address}
											</li>
											{nextEvent.event_type && (
												<li className="nav-item">
													<i className="bi bi-tag pe-1"></i> {nextEvent.event_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
												</li>
											)}
											{nextEvent.sponsor && (
												<li className="nav-item">
													<i className="bi bi-award pe-1"></i> Sponsored by {nextEvent.sponsor.name}
												</li>
											)}
										</ul>
										{nextEvent.description && (
											<p className="text-muted small mt-2 mb-0">
												{nextEvent.description.length > 100
													? `${nextEvent.description.substring(0, 100)}...`
													: nextEvent.description}
											</p>
										)}
									</div>
								</div>
							</div>
						) : (
							<p>No upcoming events found.</p>
						)}
					</div>
				</div>
				{/* Events END */}
				{/* Games START */}
				<div className="card mt-3">
					<div className="card-header d-sm-flex align-items-center justify-content-between border-0 pb-0">
						<h5 className="card-title mb-sm-0">Upcoming Games</h5>
					</div>
					{/* <div className="card-body pb-0">
						<div className="alert alert-success alert-dismissible fade show" role="alert">
							<strong>Announcement:</strong> Weather all clear: Games still scheduled.
							<button type="button" className="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
						</div>
					</div> */}
					<div className="card-body pt-1">
						{games.length > 0 ? (
							games.map((game) => {
								// Extract team participants from the new API structure
								const homeParticipant = game.teams?.find((p) => p.is_home) || {};
								const awayParticipant = game.teams?.find((p) => p.is_away) || {};
								const isByeGame = !awayParticipant.participant_name || game.game_type === 'bye';
								const userParticipant = game.players?.find(
									(p) => user?.players?.some(player => player.id === p.object_id)
								) || {};

								return (
									<div key={game.id} className="row align-items-center mb-4 mb-lg-2 pb-4 pb-lg-2">
										<div className="col-6 col-lg-2 order-1">
											<div className="avatar avatar-xl">
												<div className="position-relative">
													<div
														style={{
															width: '100px',
															height: '100px',
															margin: 'auto',
															display: 'block',
															position: 'relative',
															overflow: 'hidden',
															borderRadius: '8px',
														}}
													>
														{isByeGame ? (
															// For bye games, show only home team logo
															homeParticipant.participant_logo ? (
																<img
																	src={homeParticipant.participant_logo}
																	alt={`${homeParticipant.participant_name} logo`}
																	style={{
																		width: '100%',
																		height: '100px',
																		objectFit: 'cover',
																	}}
																/>
															) : (
																// Placeholder for bye games
																<div
																	style={{
																		width: '100%',
																		height: '200px',
																		backgroundColor: '#f8f9fa',
																		display: 'flex',
																		alignItems: 'center',
																		justifyContent: 'center',
																	}}
																>
																	<i className="bi bi-people-fill" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
																</div>
															)
														) : (
															<>
																{homeParticipant.participant_logo ? (
																	<img
																		src={homeParticipant.participant_logo}
																		alt={`${homeParticipant.participant_name} logo`}
																		style={{
																			width: '100px',
																			height: '100px',
																			objectFit: 'cover',
																			position: 'absolute',
																			left: 0,
																			top: 0,
																			clipPath: 'polygon(0 0, 100% 0, 100% 50%, 0 50%)',
																		}}
																	/>
																) : (
																	<div
																		style={{
																			width: '100%',
																			height: '50%',
																			backgroundColor: 'blue',
																			position: 'absolute',
																			top: 0,
																		}}
																	></div>
																)}

																{awayParticipant.participant_logo ? (
																	<img
																		src={awayParticipant.participant_logo}
																		alt={`${awayParticipant.participant_name} logo`}
																		style={{
																			width: '100px',
																			height: '100px',
																			objectFit: 'cover',
																			position: 'absolute',
																			left: 0,
																			bottom: 0,
																			clipPath: 'polygon(0 50%, 100% 50%, 100% 100%, 0 100%)',
																		}}
																	/>
																) : (
																	<div
																		style={{
																			width: '100%',
																			height: '50%',
																			backgroundColor: 'red',
																			position: 'absolute',
																			bottom: 0,
																		}}
																	></div>
																)}
															</>
														)}
													</div>
												</div>
											</div>
										</div>
										<div className="col-12 col-lg-6 order-3 order-lg-2">
											{isByeGame ? (
												<div className="justify-content-center mt-2 mt-sm-0">
													<h5 className="mb-1">
														<Link className="nav-link" to={`/games/${game.id}`}>
															{homeParticipant.participant_name} (BYE)
														</Link>
													</h5>
												</div>
											) : (
												<div className="justify-content-center mt-2 mt-sm-0">
													<h5 className="mb-1">
														<Link className="nav-link" to={`/games/${game.id}`}>
															{game.home_team} vs. {game.away_team}
														</Link>
													</h5>
													<ul className="nav nav-stack small">
														<li className="nav-item">
															<i className="bi bi-calendar-check pe-1"></i> {game.scheduled_date_only} at {game.scheduled_time_only}
														</li>
														<li className="nav-item">
															<i className="bi bi-geo-alt pe-1"></i> {game.location.name}
														</li>
													</ul>
												</div>
											)}
										</div>
										{!isByeGame && (
											<div className="col-6 col-lg-3 order-2 order-lg-3 text-end">
												<div className="ms-sm-auto mt-2 mt-sm-0">
													<input
														type="checkbox"
														className="btn-check"
														id={`Interested-${userParticipant.id}`}
														checked={userParticipant.attending || false}
														onChange={() => handleAttendingToggle(game.id, userParticipant.id, userParticipant.attending)}
													/>
													<label
														className={`btn btn-sm ${userParticipant.attending ? 'btn-success' : 'btn-outline-success'}`}
														htmlFor={`Interested-${userParticipant.id}`}
													>
														<FaThumbsUp className="me-1" /> {userParticipant.attending ? "Attending" : "Not Attending"}
													</label>
												</div>
											</div>
										)}
									</div>
								);
							})
						) : (
							<p>No upcoming games found.</p>
						)}

						{/* View More Games Button */}
						{hasMoreGames && games.length > 0 && (
							<div className="text-center mt-3">
								<Button
									variant="outline-primary"
									size="sm"
									onClick={handleLoadMoreGames}
									disabled={loadingMoreGames}
								>
									{loadingMoreGames ? (
										<>
											<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
											Loading...
										</>
									) : (
										`View More Games (${totalGames - games.length} remaining)`
									)}
								</Button>
							</div>
						)}
					</div>
				</div>
				{/* Games END */}
			</div>

			{/* Add Team Modal */}
			<AddTeamModal
				show={showCreateTeamModal}
				handleClose={() => setShowCreateTeamModal(false)}
				onTeamAdded={handleTeamAdded}
			/>
		</div>
	);
}

export default ProfileFeed;
